import { computed, inject, provide } from "vue";
import { useStore } from "@/lib/vuex-composition";

export const injectKey = Symbol();

export function useJobWarnings(provideState = false) {
  const store = useStore();

  const job = computed(() => {
    return store.state.job;
  });

  const jobStatusEnabled = computed(() => {
    return store.getters.jobStatusEnabled;
  });

  async function withImportedDataWarning({ action, record, context }) {
    await store.dispatch("withImportedDataWarning", {
      action,
      record,
      context
    });
  }

  async function withJobStatusFinalWarning({ action, record, context }) {
    await store.dispatch("withJobStatusFinalWarning", {
      action,
      record,
      context
    });
  }

  async function withJobWarnings({ action, record, context }) {
    let noWarningTriggered = true;

    for (const warning of warnings(action, record, context)) {
      try {
        if (warning.condition) {
          noWarningTriggered = false;
          await warning.handler();
          break;
        }
      } catch (e: any) {
        if (e.result !== "cancel") {
          throw e;
        }
      }
    }

    if (noWarningTriggered) {
      await action();
    }
  }

  function warnings(
    action: () => void,
    record: { imported: boolean },
    context: string
  ) {
    return [
      {
        condition: record.imported,
        handler: () =>
          withImportedDataWarning({
            action,
            record,
            context
          })
      },
      {
        condition: jobStatusEnabled.value && job.value.status?.final,
        handler: () =>
          withJobStatusFinalWarning({
            action,
            record: job.value,
            context
          })
      }
    ];
  }

  const jobWarningsInterface = {
    withJobWarnings
  };

  if (provideState) provide(injectKey, jobWarningsInterface);
  return jobWarningsInterface;
}

export const injectUseJobWarnings = () =>
  inject<ReturnType<typeof useJobWarnings>>(injectKey)!;
